import { useMemo } from 'react';
import { useStore } from 'react-redux';
import { refreshUserToken } from '@rsos/sinatra';
import { getUrlParameter } from '@rsos/utils';

// This 541 response status code is deprecated. The frontend used to refresh the
// browser window upon detecting this code. It is believed to be deprecated and
// no longer used. We're throwing an explicit error upon encountering it to help
// identify if any code paths are still triggering it. If after January 3, 2026
// no such errors are observed in sentry, this code and related handling must be
// removed.
const DEPRECATED_541_STATUS_CODE = 541;
const DEPRECATED_541_MESSAGE =
  'Unexpected deprecated application status code 541 repsonse';

/**
 * createFetchRetry returns a function that closes over the given store and options.
 * fetchRetry attempts the given fetch request, automatically
 * appending the access token from the redux store. If the request
 * fails because of expired credentials, an automatic attempt to
 * refresh the token is done before retrying the original request.
 * @param {Object} store - A reference to the redux store
 * @param {Object} [options] - Configuration options for fetchRetry behavior
 * @param {boolean} [options.shouldThrowResponse=true] - When true, non-ok responses are thrown as errors.
 *                                                        When false, responses are returned without throwing (standard fetch behavior).
 * @returns {function} - fetchRetry
 */
export const createFetchRetry =
  (store, { shouldThrowResponse = true } = {}) =>
  (url, options = {}) => {
    // NOTE the access token is read here to guarantee that it's the
    // latest value in the store.

    const state = store.getState();
    const { token } = state.sinatra.auth.tokens;

    const accessToken = getUrlParameter('access_token');
    const productToken = token || accessToken;

    const defaults = {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Authorization: `Bearer ${productToken}`,
      },
    };

    const configuredOptions = {
      ...defaults,
      ...options,
    };

    if (options.headers) {
      configuredOptions.headers = {
        ...defaults.headers,
        ...options.headers,
      };
    }

    return fetch(url, configuredOptions).then(response => {
      if (response.ok) {
        return response;
      }

      if (response.status === DEPRECATED_541_STATUS_CODE) {
        throw new Error(DEPRECATED_541_MESSAGE);
      }

      if (response.status !== 401 && response.status !== 403) {
        if (shouldThrowResponse) {
          throw response;
        }
        return response;
      }

      const { refresh_token } = store.getState().sinatra.auth.tokens;
      return store.dispatch(refreshUserToken(refresh_token)).then(() => {
        const { token } = store.getState().sinatra.auth.tokens;
        const newOptions = {
          ...options,
          headers: {
            ...options.headers,
            Authorization: `Bearer ${token}`,
          },
        };
        return fetch(url, newOptions).then(response => {
          if (response.ok) {
            return response;
          }
          if (response.status === DEPRECATED_541_STATUS_CODE) {
            throw new Error(DEPRECATED_541_MESSAGE);
          }
          if (shouldThrowResponse) {
            throw response;
          }
          return response;
        });
      });
    });
  };

/**
 * React hook that provides a fetchRetry function with the Redux store already bound.
 * @param {Object} [options] - Configuration options for fetchRetry behavior
 * @param {boolean} [options.shouldThrowResponse=true] - When true, non-ok responses are thrown as errors.
 * @returns {function} - fetchRetry function
 */
export const useFetchRetry = ({ shouldThrowResponse } = {}) => {
  const store = useStore();
  return useMemo(
    () => createFetchRetry(store, { shouldThrowResponse }),
    [store, shouldThrowResponse],
  );
};
